//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import { IMlsMediaItem } from '@models/IMlsMediaItem';

export const TWITTER_API_DATE_FORMAT = 'YYYY MM DD';

// TODO: use api code-gen when ready
export interface IMaterializedTweetWithMetrics {
  cuid: Cuid;
  mediaItem?: IMlsMediaItem;
  metrics?: ITwitterMetrics;
  tweet?: ITwitterTweet;
}

export interface ITwitterMetrics {
  impressions: number;
  likes: number;
  replies: number;
  retweets: number;
  linkClicks: number;
  followerEngagementRate: number;
}

export interface ITwitterTweet {
  createdAt: number;
  tweetId: string;
  poster: ITwitterUser;
  tweetText: string;
  tags: string[];
  favoriteCount: number;
  retweetCount: number;
  location?: ITwitterTweetLocation;
  inReplyTo?: ITwitterUser;
  media: ITwitterTweetMedia[];
  contextAnnotations?: ITwitterTweetContextAnnotation[];
  conversationId?: string;
  geo?: ITwitterTweetGeo;
  lang?: string;
  possiblySensitive?: boolean;
  referencedTweets?: ITwitterTweetReferencedTweet[];
  replySettings?: TwitterTweetReplySetting;
  source?: string;
  tweetMetrics?: ITwitterTweetMetrics;
  withheld?: ITwitterEntityWithheld;
}

export interface ITwitterUser {
  id: string;
  screenName: string;
  description?: string;
  location?: string;
  name?: string;
  pinnedTweetId?: string;
  profileImageUrl?: string;
  protected?: boolean;
  url?: string;
  userMetrics?: ITwitterUserMetrics;
  verified?: boolean;
  withheld?: ITwitterEntityWithheld;
}

export interface ITwitterUserMetrics {
  publicMetrics?: ITwitterUserPublicMetrics;
}

export interface ITwitterUserPublicMetrics {
  followersCount: number;
  followingCount: number;
  tweetCount: number;
  listedCount: number;
}

export interface ITwitterEntityWithheld {
  copyright: boolean;
  countryCodes: string[];
  scope: TwitterWithheldScope;
}

export enum TwitterWithheldScope {
  Tweet = 'tweet',
  User = 'user',
}

export interface ITwitterTweetLocation {
  latitude: number;
  longitude: number;
}

export interface ITwitterTweetMedia {
  httpsUrl: string;
  httpUrl: string;
  id: string;
  mediaType: TwitterMediaType;
  mediaTypeRaw: string;
  height?: number;
  width?: number;
  viewCount?: number;
  durationMillis?: number;
  mediaMetrics?: ITwitterTweetMediaMetrics;
}

export enum TwitterMediaType {
  Photo = 'photo',
  Video = 'video',
  AnimatedGif = 'animated_gif',
  Unknown = '',
}

export interface ITwitterTweetMediaMetrics {
  nonPublicMetrics?: ITwitterTweetMediaNonPublicMetrics;
  organicMetrics?: ITwitterTweetMediaOrganicMetrics;
  promotedMetrics: ITwitterTweetMediaPromotedMetrics;
  publicMetrics?: ITwitterTweetMediaPublicMetrics;
}

export interface ITwitterTweetMediaNonPublicMetrics {
  playback_0_count: number;
  playback_100_count: number;
  playback_25_count: number;
  playback_50_count: number;
  playback_75_count: number;
}

export interface ITwitterTweetMediaPublicMetrics {
  viewCount: number;
}

export interface ITwitterTweetMediaOrganicMetrics
  extends ITwitterTweetMediaNonPublicMetrics,
    ITwitterTweetMediaPublicMetrics {}

export interface ITwitterTweetMediaPromotedMetrics
  extends ITwitterTweetMediaNonPublicMetrics,
    ITwitterTweetMediaPublicMetrics {}

export interface ITwitterTweetContextAnnotation {
  domain: ITwitterTweetDomainAndEntity;
  entity: ITwitterTweetDomainAndEntity;
}

export interface ITwitterTweetDomainAndEntity {
  id: string;
  name: string;
  description?: string;
}

export interface ITwitterTweetGeo {
  coordinates?: ITwitterTweetCoordinates;
  placeId?: string;
}

export enum TwitterCoordinatesType {
  Point = 'Point',
}

export interface ITwitterTweetCoordinates {
  type: TwitterCoordinatesType;
  coordinates: [number, number];
}

export enum TwitterReferencedTweetType {
  Retweeted = 'retweeted',
  Quoted = 'quoted',
  RepliedTo = 'replied_to',
}

export interface ITwitterTweetReferencedTweet {
  type: TwitterReferencedTweetType;
  id: string;
}

export enum TwitterTweetReplySetting {
  Everyone = 'everyone',
  MentionedUsers = 'mentioned_users',
  Followers = 'followers',
}

export interface ITwitterTweetMetrics {
  nonPublicMetrics?: ITwitterTweetNonPublicMetrics;
  organicMetrics?: ITwitterTweetOrganicMetrics;
  promotedMetrics?: ITwitterTweetPromotedMetrics;
  publicMetrics?: ITwitterTweetPublicMetrics;
}

export interface ITwitterTweetNonPublicMetrics {
  impressionCount: number;
  urlLinkClicks?: number;
  userProfileClicks: number;
}

export interface ITwitterTweetOrganicMetrics {
  impressionCount: number;
  replyCount: number;
  retweetCount: number;
  likeCount: number;
  urlLinkClicks?: number;
  userProfileClicks: number;
}

export interface ITwitterTweetPromotedMetrics {
  impressionCount: number;
  likeCount: number;
  replyCount: number;
  retweetCount: number;
  urlLinkClicks?: number;
  userProfileClicks: number;
}

export interface ITwitterTweetPublicMetrics {
  retweetCount: number;
  replyCount: number;
  likeCount: number;
  quoteCount: number;
}

export interface ITwitterEngagementMetrics {
  impressions?: number;
  engagements?: number;
  linkClicks?: number;
  avgEngagementRate?: number;
}

export interface ITwitterDailyEngagementMetric {
  date: string;
  metrics: ITwitterEngagementMetrics;
}

export interface ITwitterEngagementSummaryStats {
  impressions?: number;
  engagements?: number;
  linkClicks?: number;
  avgEngagementRate?: number;
}

export enum TwitterMetricSortKey {
  PostedTimestamp = 'PostedTimestamp',
  Impressions = 'Impressions',
  Likes = 'Likes',
  Replies = 'Replies',
  Retweets = 'Retweets',
  LinkClicks = 'LinkClicks',
  FollowerEngagementRate = 'EngagementRate',
}

export interface ITwitterAudienceDailyStats {
  followers?: number;
  changeInFollowers?: number;
  userProfileClicks?: number;
}

export interface ITwitterAudienceSummaryStats {
  changeInFollowers?: number;
  userProfileClicks?: number;
}

export interface IGetAudienceSummaryStatsRequest {
  twitterUserId: string;
  twitterUsername: string;
  startTime: Date;
  endTime: Date;
}

export interface IGetAudienceDailyStatsRequest {
  twitterUserId: string;
  startTime: Date;
  endTime: Date;
}

export interface IGetEngagementMetricsRequest {
  twitterUsername: string;
  twitterUserId: string;
  startTime: DateString;
  endTime: DateString;
}

export interface IGetUserTweetsRequest {
  twitterUsername: string;
  twitterUserId: string;
  startTime: DateString;
  endTime: DateString;
  limit?: number;
  offset?: number;
  sortKey?: string;
  sortDirection?: string;
}

export interface IGetAudienceDailyStatsResponse {
  data: {
    dailyMetrics: Record<string, ITwitterAudienceDailyStats>;
    earliestMetricsDate: string;
  };
}

export interface IGetAudienceSummaryStatsResponse {
  data: ITwitterAudienceSummaryStats;
}

export interface IGetEngagementMetricsResponse {
  data: {
    dailyMetrics: ITwitterDailyEngagementMetric[];
    summaryMetrics: ITwitterEngagementSummaryStats;
  };
}

export interface IGetUserTweetsResponse {
  data: {
    tweets: IMaterializedTweetWithMetrics[];
    total: number;
  };
}
